import type { BookingList, BookingStage } from '../api/bookings';

/**
 * The saved filters the register offers as tabs.
 *
 * <p>
 * <strong>They are the questions a counter asks each morning</strong> — who is collecting today,
 * who is bringing something back, the same pair again for tomorrow, and the two that mean somebody
 * has to do something. §8 lists "today's pickups and today's returns" as reports; they are far
 * more useful as the default way into the list than as something to construct out of a stage and a
 * date filter.
 * </p>
 *
 * <p>
 * A tab is a filter, never a separate endpoint. That keeps paging, sorting and search working
 * identically across every one of them, and it means a tab added later is an entry here rather than
 * a new query on the server — which is exactly what the two tomorrow views cost.
 * </p>
 */
export interface RegisterView {
  id: string;
  label: string;
  filters: {
    stage?: BookingStage;
    /**
     * Overdue is a filter and not a stage, the same distinction the contract draws: an issued
     * booking whose return date has passed, computed against the shop's date rather than stored.
     */
    overdue?: boolean;
    pickupsOn?: string;
    returnsOn?: string;
    inAlteration?: boolean;
  };
}

/**
 * "Open the register ready to take a booking", riding the view channel.
 *
 * <p>
 * <strong>An intent rather than a filter</strong>, and it lives here — beside the ids it must not
 * collide with — precisely because that is the one way it can go wrong. Every other value the
 * channel carries is a saved view; this one opens the create screen and leaves the register on All
 * underneath. It travels that way because the shell has no route parameters, the same limitation
 * `ScreenContext.find` already documents, and a third navigation channel for one button is the
 * larger change.
 * </p>
 *
 * <p>
 * Deliberately not a view, so {@link filtersForView} falls back to the unfiltered list on its own.
 * `registerViews.test.ts` asserts no tab ever claims this id — the day one did, the button would
 * quietly become a filter and the create screen would never open.
 * </p>
 */
export const takeBookingIntent = 'new';

/**
 * The day after a shop's date.
 *
 * <p>
 * Derived from the date passed in rather than from `new Date()`, which is the rule the whole file
 * follows: the register's "today" is fixed once when a screen mounts, and a second reading of the
 * clock is how a tab and the list under it end up describing different days.
 * </p>
 */
export function dayAfter(today: string): string {
  const parsed = new Date(`${today}T00:00:00Z`);

  // UTC arithmetic on purpose. These are calendar dates — rule 14's deliberate exception — and
  // `setDate` on a local Date crosses a DST boundary in some zones by adding 23 or 25 hours.
  parsed.setUTCDate(parsed.getUTCDate() + 1);

  return parsed.toISOString().slice(0, 10);
}

/** `today` is the shop's date, passed in rather than read here so nothing depends on a browser clock. */
export function registerViews(today: string): RegisterView[] {
  const tomorrow = dayAfter(today);

  return [
    { id: 'all', label: 'All', filters: {} },
    { id: 'pickups', label: "Today's pickups", filters: { pickupsOn: today } },
    { id: 'returns', label: "Today's returns", filters: { returnsOn: today } },
    /*
      Tomorrow is a tab and not only a card on the morning screen.

      A shop lays tomorrow's garments out the evening before — in wedding season that is the work,
      not an optimisation — and the card can only ever show the first few. Without a tab there is
      nowhere to send somebody for the rest, and the alternative offered was a "view all" that
      opened today's list instead, which is a wrong answer rather than a missing one.
    */
    { id: 'pickupsTomorrow', label: "Tomorrow's pickups", filters: { pickupsOn: tomorrow } },
    { id: 'returnsTomorrow', label: "Tomorrow's returns", filters: { returnsOn: tomorrow } },
    { id: 'alteration', label: 'In alteration', filters: { inAlteration: true } },
    { id: 'overdue', label: 'Overdue', filters: { overdue: true } },
  ];
}

/**
 * The filters for a view id, or the unfiltered list if the id is not one we know.
 *
 * Falling back rather than throwing because the view id can arrive from a remembered UI state or a
 * link, and an unrecognised one should show the register rather than an error.
 */
export function filtersForView(view: string, today: string): RegisterView['filters'] {
  return registerViews(today).find((candidate) => candidate.id === view)?.filters ?? {};
}

/**
 * Where the Bookings board opens for a view a Today card names (the board replaced the register's
 * seven tabs on 2026-10-22): which of Coming · Out · Done, and which day of its week strip — or
 * `late` for the late ones, the banner's "Show the late". In alteration has no day of its own, so it
 * opens Coming with that filter on.
 */
export interface BoardStart {
  list: BookingList;
  // A date, or `late` for the late ones.
  day: string | null;
  inAlteration: boolean;
}

export function boardStart(view: string | undefined, today: string): BoardStart {
  const tomorrow = dayAfter(today);
  const start = (list: BookingList, day: BoardStart['day'] = null): BoardStart => ({
    list,
    day,
    inAlteration: false,
  });

  switch (view) {
    case 'pickups':
      return start('Upcoming', today);
    case 'pickupsTomorrow':
      return start('Upcoming', tomorrow);
    case 'returns':
      return start('WithCustomers', today);
    case 'returnsTomorrow':
      return start('WithCustomers', tomorrow);
    case 'overdue':
      return start('WithCustomers', 'late');
    case 'alteration':
      return { ...start('Upcoming'), inAlteration: true };
    default:
      return start('Upcoming');
  }
}
